import { animated } from '@react-spring/web';
import styled from 'styled-components';
import { createShouldForwardStyledProp, modeColor } from '../../styles';
import { Z_INDEX } from '../../styles/layout';
import { Box, Flex, Grid, Stack, type GridProps } from '../Core';
import { useDrawerStyleContext } from './DrawerStyleContext';

type WrapperProps = { position: 'absolute' | 'fixed' | 'relative'; width?: number; placement: 'left' | 'right' };

export const Wrapper = styled(animated.div)<WrapperProps>`
  height: 100%;
  background: var(--backgroundDrawer);
  backdrop-filter: var(--backdropFilterDrawer);
  z-index: ${Z_INDEX.drawer};
  ${({ width }) => width !== null && `width: ${width}px`};
  min-width: min-content;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  ${({ position, placement }) =>
    position === 'fixed' &&
    `
    position: fixed;
    top: 0;
    bottom: 0;
    ${placement === 'left' ? `left: 0;` : `right: 0`};
  `};

  ${({ position, placement, theme }) =>
    position === 'absolute' &&
    `
    position: absolute;
    top: 0;
    bottom: 0;
    box-shadow: 0 0 40px ${modeColor(theme, theme.colors.black.mute, theme.colors.black.dim)};
    ${placement === 'left' ? `left: 0;` : `right: 0`};
  `};

  ${({ position }) =>
    position === 'relative' &&
    `
    position: relative;
  `};
`;

/** 3-part Drawer header using CSS GRid */
export const DrawerHeader = styled(Grid)`
  display: grid;
  grid-template-columns: ${({ gridTemplateColumns }) => gridTemplateColumns ?? '1fr 1fr 1fr'};
  padding: ${({ p, theme }) => p ?? `0 16px`};
  min-height: 3.5rem;
  border-bottom: solid 1px var(--colors-gray-010);
  flex: 0;
  align-items: center;
`;

/** Simple 2-part Drawer Header using Flexbox */
export const DrawerHeaderFlex = styled(Flex)`
  justify-content: space-between;
  padding: 8px 16px;
  min-height: 3.5rem;
  border-bottom: solid 1px var(--colors-gray-010);
  align-items: center;
`;

export const DrawerContent = (props: GridProps) => {
  const { drawerHeaderDesign } = useDrawerStyleContext();
  return <StyledDrawerContent {...props} p={props.p ?? (drawerHeaderDesign === 'flex' ? 'spacing12' : 'spacing16')} />;
};

const StyledDrawerContent = styled(Grid)``;
StyledDrawerContent.defaultProps = {
  overflow: 'hidden',
  flex: 1,
  pb: '0',
};

export const DrawerFooter = styled(Grid)`
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  border-top: solid 1px var(--colors-gray-010);
`;
DrawerFooter.defaultProps = {
  p: 'spacing16',
  gap: 'spacing16',
};

export const DrawerFooterFlex = styled(Flex)`
  border-top: solid 1px var(--colors-gray-010);
`;
DrawerFooterFlex.defaultProps = {
  p: 'spacing16',
  gap: 'spacing16',
};

const noForwardProps = new Set(['width']);

export const DrawerWrapper = styled(Box).withConfig({
  shouldForwardProp: createShouldForwardStyledProp(noForwardProps),
})<{ width?: number }>`
  background: var(--backgroundDrawer);
  transition:
    width 200ms ease,
    margin 200ms ease;
  width: ${({ width }) => (width != null ? `${width}px` : 'auto')};
  height: 100%;
  overflow: hidden;
  flex: 0 0 auto;

  @media print {
    display: none;
  }
`;

export const DrawerContentWrapper = styled(Stack).withConfig({
  shouldForwardProp: createShouldForwardStyledProp(noForwardProps),
})<{ width?: number }>`
  position: relative;
  width: ${({ width }) => (width != null ? `${width}px` : 'auto')};
  height: 100%;
  overflow: hidden;
`;
